import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Home, Loader2, Gift, Link2, Copy, MessageCircle, Trash2, Search,
  FileSpreadsheet, FileText, MapPin, Phone, Inbox, MapPinned, ChevronDown,
} from "lucide-react";
import { authGet, authPut, authDelete, isAdvisor, getAuthToken } from "../utils/auth";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { BrandingSelect, BrandingSummary, BulkBrandingBar, SentToggle } from "../components/gifts/GiftBranding";
import { GiftFilters, EMPTY_FILTERS } from "../components/gifts/GiftFilters";
import { GiftIndiaMap } from "../components/gifts/GiftIndiaMap";
import MobileBottomNav from "../components/MobileBottomNav";
import MobileSidebar from "../components/MobileSidebar";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

const ShareCard = ({ link, open, onToggle, toggling }) => {
  const copy = () => { navigator.clipboard.writeText(link); toast.success("Link copied! Share it with your clients."); };
  const whatsapp = () => {
    const text = encodeURIComponent(`Happy Diwali! 🪔 We'd love to send you a small Diwali gift from Kavach Box. Please share your delivery address here:\n\n${link}`);
    window.open(`https://api.whatsapp.com/send?text=${text}`, "_blank");
  };
  return (
    <div className="bg-gradient-to-r from-[#EDF1F8]/60 to-[#EDF1F8] border border-[#D4A017]/30 rounded-2xl p-5 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2"><Link2 className="h-5 w-5 text-[#A87608]" /><h2 className="font-semibold">Your Diwali gift form link</h2></div>
        <label className="flex items-center gap-2 text-sm font-medium" data-testid="gift-open-toggle-label">
          {toggling && <Loader2 className="h-4 w-4 animate-spin" />}
          <span className={open ? "text-emerald-600" : "text-red-600"} data-testid="gift-form-state">{open ? "Accepting responses" : "Closed"}</span>
          <Switch checked={open} onCheckedChange={onToggle} disabled={toggling} data-testid="gift-open-toggle" />
        </label>
      </div>
      <p className="text-sm text-[#33436B] mb-3">Share this link on WhatsApp. Clients fill in their name, mobile and address; no login needed. If someone fills it twice with the same mobile number, their entry is updated.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 flex items-center gap-2 bg-[#F4F6FA] border border-[#C9D4E6] rounded-xl px-3 py-2.5 min-w-0">
          <Gift className="h-4 w-4 text-[#A87608] shrink-0" />
          <span className="text-sm text-[#2E3B57] truncate" data-testid="gift-share-link">{link}</span>
        </div>
        <div className="flex gap-2">
          <button onClick={copy} data-testid="gift-copy-link-btn" className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#EDF1F8] hover:bg-[#E2E9F4] text-sm font-medium transition-colors"><Copy className="h-4 w-4" /> Copy</button>
          <button onClick={whatsapp} data-testid="gift-share-whatsapp-btn" className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors"><MessageCircle className="h-4 w-4" /> WhatsApp</button>
        </div>
      </div>
    </div>
  );
};

const AddressBlock = ({ e }) => (
  <div className="min-w-0">
    <p className="text-sm text-[#2E3B57] whitespace-pre-wrap break-words">{e.address}</p>
    {e.landmark && <p className="text-xs text-[#3A4557] mt-0.5">Landmark: {e.landmark}</p>}
    <p className="text-xs font-semibold text-[#1B2A4A] mt-1 flex items-center gap-1"><MapPin className="h-3 w-3 text-[#A87608]" />{e.city}, {e.state} · {e.pincode}</p>
  </div>
);

const GRID = "grid grid-cols-1 md:grid-cols-[1.1fr_1fr_1.8fr_210px_auto] gap-3 md:gap-5";
const checkCls = "mt-1 border-[#1B2A4A] data-[state=checked]:bg-[#1B2A4A] data-[state=checked]:text-white";

const EntryRow = ({ e, onDelete, busy, selected, onSelect, onBranding, onSent }) => (
  <div className={`flex gap-3 px-4 py-4 border-b border-[#C9D4E6] last:border-0 transition-colors ${selected ? "bg-[#D4A017]/10" : e.sent_at ? "bg-emerald-50/70" : "hover:bg-white/60"}`} data-testid={`gift-entry-${e.id}`}>
    <Checkbox checked={selected} onCheckedChange={() => onSelect(e.id)} className={checkCls} data-testid={`gift-entry-select-${e.id}`} />
    <div className={`flex-1 min-w-0 ${GRID}`}>
      <div className="min-w-0">
        <p className="font-semibold truncate" data-testid={`gift-entry-name-${e.id}`}>{e.name}</p>
        <p className="text-xs text-[#3A4557]">{fmtDate(e.updated_at)}{e.submit_count > 1 && <span className="ml-1.5 px-1.5 rounded bg-[#D4A017]/15 text-[#A87608] font-semibold">Updated</span>}</p>
      </div>
      <div className="text-sm">
        <a href={`tel:+91${e.mobile}`} className="flex items-center gap-1.5 font-medium hover:text-[#A87608]" data-testid={`gift-entry-mobile-${e.id}`}><Phone className="h-3.5 w-3.5" />{e.mobile}</a>
        {e.alt_mobile && <p className="text-xs text-[#3A4557] mt-0.5 pl-5">Alt: {e.alt_mobile}</p>}
      </div>
      <AddressBlock e={e} />
      <BrandingSelect value={e.branding} onChange={(v) => onBranding([e.id], v)} testId={`gift-entry-branding-${e.id}`} />
      <div className="flex md:flex-col items-start md:items-end gap-2">
        <SentToggle sentAt={e.sent_at} onToggle={(v) => onSent([e.id], v)} testId={`gift-entry-sent-${e.id}`} />
        <button onClick={() => onDelete(e)} disabled={busy} data-testid={`gift-entry-delete-${e.id}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-600 text-xs font-medium transition-colors">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Delete
        </button>
      </div>
    </div>
  </div>
);

const DownloadButtons = ({ onDownload, downloading, disabled }) => (
  <div className="flex gap-2">
    {[["xlsx", "Excel", FileSpreadsheet, "bg-emerald-600 hover:bg-emerald-700"], ["pdf", "PDF", FileText, "bg-[#1B2A4A] hover:bg-[#2A3C63]"]].map(([fmt, label, Icon, cls]) => (
      <button key={fmt} onClick={() => onDownload(fmt)} disabled={disabled || !!downloading} data-testid={`gift-download-${fmt}-btn`} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-medium disabled:opacity-50 transition-colors ${cls}`}>
        {downloading === fmt ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />} {label}
      </button>
    ))}
  </div>
);

export default function DiwaliGiftsAdmin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [selected, setSelected] = useState([]);
  const [brandingCounts, setBrandingCounts] = useState(null);
  const [sentCounts, setSentCounts] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filterOptions, setFilterOptions] = useState(null);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showMap, setShowMap] = useState(false);
  const shareLink = `${window.location.origin}/diwali-gift`;
  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    return p.toString();
  }, [q, filters]);
  const filtered = qs.length > 0;

  const load = async () => {
    try {
      const res = await authGet(`/api/advisor/gifts/diwali?${qs}`);
      if (res.ok) {
        setEntries(res.data.entries || []); setTotal(res.data.total || 0); setOpen(res.data.open !== false);
        setBrandingCounts(res.data.branding_counts);
        setSentCounts(res.data.sent_counts);
        setFilterOptions(res.data.filter_options);
        setFilteredTotal(res.data.filtered_total || 0);
        setRefreshKey((k) => k + 1);
      }
    } catch { toast.error("Could not load responses"); }
    setLoading(false);
  };

  useEffect(() => {
    if (!isAdvisor()) { navigate("/advisor/dashboard"); return undefined; }
    setSelected([]);
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs]);

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = entries.length > 0 && selected.length === entries.length;
  const toggleAll = () => setSelected(allSelected ? [] : entries.map((e) => e.id));
  useEffect(() => { setSelected((s) => s.filter((id) => entries.some((e) => e.id === id))); }, [entries]);

  const setBranding = async (ids, branding, bulk = false) => {
    setBulkBusy(true);
    const res = await authPut("/api/advisor/gifts/diwali/branding", { ids, branding }).catch(() => ({ ok: false }));
    if (res.ok) {
      setEntries((es) => es.map((e) => (ids.includes(e.id) ? { ...e, branding } : e)));
      toast.success(ids.length > 1 ? `Branding set for ${ids.length} clients` : "Branding saved");
      if (bulk) setSelected([]);
      load();
    } else toast.error("Could not save branding");
    setBulkBusy(false);
  };

  const setSent = async (ids, sent, bulk = false) => {
    if (!sent && !bulk && !window.confirm("Mark this gift as not sent?")) return;
    setBulkBusy(true);
    const res = await authPut("/api/advisor/gifts/diwali/sent", { ids, sent }).catch(() => ({ ok: false }));
    if (res.ok) {
      const label = sent ? "sent" : "not sent";
      toast.success(ids.length > 1 ? `${ids.length} clients marked as ${label}` : `Marked as ${label}`);
      if (bulk) setSelected([]);
      load();
    } else toast.error("Could not update sent status");
    setBulkBusy(false);
  };

  const toggleOpen = async (next) => {
    setToggling(true);
    const res = await authPut("/api/advisor/gifts/diwali/settings", { open: next }).catch(() => ({ ok: false }));
    if (res.ok) { setOpen(next); toast.success(next ? "Form is open again" : "Form closed. New responses are blocked."); }
    else toast.error("Could not update the form");
    setToggling(false);
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete ${e.name}'s address permanently?`)) return;
    setBusyId(e.id);
    const res = await authDelete(`/api/advisor/gifts/diwali/${e.id}`).catch(() => ({ ok: false }));
    if (res.ok) { toast.success("Deleted"); load(); } else toast.error("Failed to delete");
    setBusyId(null);
  };

  const download = async (fmt) => {
    setDownloading(fmt);
    try {
      const res = await fetch(`${API}/api/advisor/gifts/diwali/export?format=${fmt}${qs ? `&${qs}` : ""}`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
      if (!res.ok) throw new Error();
      const name = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") || "")?.[1];
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = name || `diwali-gift-addresses${filtered ? "-filtered" : ""}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
    setDownloading(null);
  };

  return (
    <div className="min-h-screen bg-[#F4F6FA] text-[#1B2A4A] pb-24 md:pb-8" data-testid="diwali-gifts-admin">
      <MobileSidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} />
      <header className="sticky top-0 z-40 bg-[#F4F6FA]/95 backdrop-blur-sm border-b border-[#C9D4E6]">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-[#EDF1F8] rounded-lg" data-testid="gift-back-btn"><ArrowLeft className="h-5 w-5" /></button>
          <button onClick={() => navigate("/advisor/dashboard")} className="p-2 hover:bg-[#EDF1F8] rounded-lg" data-testid="gift-home-btn"><Home className="h-5 w-5" /></button>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-[#D4A017] to-[#D9A82C] flex items-center justify-center"><Gift className="h-5 w-5 text-[#0a1628]" /></div>
            <div>
              <h1 className="text-lg font-bold">Diwali Gifts</h1>
              <p className="text-xs text-[#3A4557]">Client delivery addresses</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-5">
        <ShareCard link={shareLink} open={open} onToggle={toggleOpen} toggling={toggling} />

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="flex items-baseline gap-2 mr-auto">
            <span className="text-3xl font-bold" data-testid="gift-total-count">{filtered ? filteredTotal : total}</span>
            <span className="text-sm text-[#3A4557]" data-testid="gift-count-label">
              {filtered ? `of ${total} addresses match your filters` : total === 1 ? "address collected" : "addresses collected"}
            </span>
          </div>
          <div className="relative sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7489]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, mobile, pin, city" data-testid="gift-search-input" className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#C9D4E6] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A017]/40" />
          </div>
          <DownloadButtons onDownload={download} downloading={downloading} disabled={entries.length === 0} />
        </div>

        {total > 0 && <BrandingSummary counts={brandingCounts} sentCounts={sentCounts} />}
        {total > 0 && <GiftFilters filters={filters} onChange={setFilters} options={filterOptions} />}
        <div className="flex flex-col gap-4 xl:grid xl:grid-cols-[minmax(0,1fr)_330px] xl:items-start xl:gap-5">
          <div className="min-w-0">
        <BulkBrandingBar count={selected.length} onApply={(b) => setBranding(selected, b, true)} onSent={(s) => setSent(selected, s, true)} onCancel={() => setSelected([])} busy={bulkBusy} />

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#A87608]" /></div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16 bg-white/40 border border-[#C9D4E6] rounded-2xl" data-testid="gift-empty-state">
            <Inbox className="h-12 w-12 text-[#3a4a6d] mx-auto mb-3" />
            <p className="text-[#33436B] font-medium">{filtered ? "No matching addresses" : "No addresses yet"}</p>
            <p className="text-[#33436B] text-sm mt-1">{filtered ? "Try a different search or clear the filters." : "Share your link above to start collecting."}</p>
          </div>
        ) : (
          <div className="bg-white/50 border border-[#C9D4E6] rounded-2xl overflow-hidden" data-testid="gift-entries-list">
            <div className="flex gap-3 px-4 py-2.5 bg-[#EDF1F8] text-xs font-semibold uppercase tracking-wide text-[#3A4557]">
              <Checkbox checked={allSelected} onCheckedChange={toggleAll} className={checkCls.replace("mt-1 ", "")} aria-label="Select all" data-testid="gift-select-all" />
              <div className={`flex-1 hidden md:grid ${GRID.replace("grid grid-cols-1 ", "")}`}>
                <span>Client</span><span>Mobile</span><span>Delivery address</span><span>Branding</span><span className="w-[100px] text-right">Gift status</span>
              </div>
              <span className="md:hidden">Select all</span>
            </div>
            {entries.map((e) => (
              <EntryRow key={e.id} e={e} onDelete={remove} busy={busyId === e.id}
                selected={selected.includes(e.id)} onSelect={toggleSelect} onBranding={setBranding} onSent={setSent} />
            ))}
          </div>
        )}
          </div>
          {total > 0 && (
            <aside className="order-first xl:order-none xl:sticky xl:top-20" data-testid="gift-map-aside">
              <button onClick={() => setShowMap((v) => !v)} data-testid="gift-map-toggle-btn"
                className="xl:hidden flex w-full items-center justify-between rounded-xl border border-[#C9D4E6] bg-white/60 px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-white">
                <span className="flex items-center gap-2"><MapPinned className="h-4 w-4 text-[#A87608]" /> {showMap ? "Hide client map" : "Show client map"}</span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${showMap ? "rotate-180" : ""}`} />
              </button>
              <div className={`${showMap ? "mt-2 block" : "hidden"} xl:mt-0 xl:block`}>
                <GiftIndiaMap qs={qs} refreshKey={refreshKey} selectedState={filters.state} onSelectState={(s) => setFilters((f) => ({ ...f, state: s, city: "" }))} />
              </div>
            </aside>
          )}
        </div>
      </main>

      <MobileBottomNav onMenuClick={() => setShowSidebar(true)} />
    </div>
  );
}
